import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { collection, getDocs, query, orderBy, where, limit } from 'firebase/firestore';
import { db } from '../firebase/config';

const JournalContainer = styled.div`
  max-width: 1180px;
  margin: 0 auto;
  padding: 0 20px;
  margin-bottom: 60px; /* Add margin to prevent footer overlap */
  
  @media (max-width: 768px) {
    padding: 0 15px;
  }
`;

const JournalHeader = styled.div`
  text-align: center;
  margin: 70px 0 45px;
  
  @media (max-width: 768px) {
    margin: 40px 0 30px;
  }
`;

const JournalTitle = styled.h1`
  font-size: 2.8rem;
  font-weight: 700;
  margin-bottom: 22px;
  
  @media (max-width: 768px) {
    font-size: 2.2rem;
    margin-bottom: 15px;
  }
`;

const JournalDescription = styled.p`
  font-size: 1.2rem;
  color: #666;
  max-width: 650px;
  margin: 0 auto;
  
  @media (max-width: 768px) {
    font-size: 1rem;
    padding: 0 10px;
  }
`;

const SearchBar = styled.div`
  display: flex;
  align-items: center;
  max-width: 550px;
  margin: 0 auto 35px;
  position: relative;
  
  @media (max-width: 768px) {
    max-width: 100%;
    margin-bottom: 25px;
  }
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 14px 18px;
  border: 1px solid #ddd;
  border-radius: 30px;
  font-size: 1.1rem;
  outline: none;
  
  &:focus {
    border-color: #ccc;
    box-shadow: 0 0 6px rgba(0, 0, 0, 0.1);
  }
  
  @media (max-width: 768px) {
    padding: 12px 15px;
    font-size: 1rem;
  }
`;

const SearchIcon = styled.div`
  position: absolute;
  right: 18px;
  top: 50%;
  transform: translateY(-50%);
  color: #999;
  font-size: 1.1rem;
`;

const FilterTabs = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 45px;
  flex-wrap: wrap;
  gap: 12px;
  
  @media (max-width: 768px) {
    margin-bottom: 30px;
    gap: 8px;
  }
`;

const FilterTab = styled.button`
  background: ${props => props.$active ? '#291759' : 'white'};
  color: ${props => props.$active ? 'white' : '#333'};
  border: none;
  padding: 10px 20px;
  border-radius: 22px;
  font-weight: 500;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${props => props.$active ? '#291759' : '#f5f5f5'};
  }
  
  @media (max-width: 768px) {
    padding: 8px 15px;
    font-size: 0.9rem;
  }
`;

const ArticlesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 35px;
  
  @media (max-width: 1100px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ArticleCard = styled.div`
  background: white;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.08);
  transition: transform 0.3s ease;
  
  &:hover {
    transform: translateY(-6px);
  }
  
  @media (max-width: 768px) {
    margin-bottom: 15px;
  }
`;

const ArticleImage = styled.img`
  width: 100%;
  height: 220px;
  object-fit: cover;
  
  @media (max-width: 768px) {
    height: 200px;
  }
`;

const ArticleContent = styled.div`
  padding: 22px;
  
  @media (max-width: 768px) {
    padding: 18px;
  }
`;

const ArticleCategory = styled.div`
  background: ${props => props.$bgColor || '#f1f8ff'};
  color: ${props => props.$color || '#291759'};
  display: inline-block;
  padding: 5px 14px;
  border-radius: 20px;
  font-size: 0.9rem;
  font-weight: 500;
  margin-bottom: 10px;
`;

const ArticleDate = styled.div`
  font-size: 0.95rem;
  color: #666;
  margin-bottom: 10px;
`;

const ArticleTitle = styled.h3`
  margin: 0 0 12px;
  font-size: 1.4rem;
  font-weight: 700;
  line-height: 1.3;
  
  @media (max-width: 768px) {
    font-size: 1.2rem;
    margin-bottom: 10px;
  }
`;

const ArticleExcerpt = styled.p`
  color: #666;
  font-size: 1rem;
  margin-bottom: 18px;
  line-height: 1.5;
`;

const ReadArticleLink = styled(Link)`
  color: #291759;
  font-weight: 600;
  text-decoration: none;
  font-size: 1rem;
  padding: 6px 0;
  display: inline-block;
  
  &:hover {
    text-decoration: underline;
  }
`;

const Journal = () => {
  const [articles, setArticles] = useState([]);
  const [activeTab, setActiveTab] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  
  const categories = ['All', 'Fitness', 'Nutrition', 'Wellness', 'Success Stories', 'Health Research'];
  
  const categoryColors = {
    'Fitness': { bg: '#fff2e6', color: '#ff6600' },
    'Nutrition': { bg: '#e6ffec', color: '#00cc44' },
    'Wellness': { bg: '#f5e6ff', color: '#9900cc' },
    'Success Stories': { bg: '#fff9e6', color: '#cc9900' },
    'Health Research': { bg: '#f2f2f2', color: '#666666' }
  };

  useEffect(() => {
    const fetchArticles = async () => {
      setLoading(true);
      try {
        // Fetch from Firebase
        const articlesCollection = collection(db, 'journal');
        let articlesQuery;
        
        if (activeTab !== 'All') {
          // Create a query with category filter first
          articlesQuery = query(
            articlesCollection, 
            where('category', '==', activeTab),
            where('status', '==', 'published'), 
            orderBy('createdAt', 'desc'), 
            limit(10)
          );
        } else {
          // Query for all articles
          articlesQuery = query(
            articlesCollection, 
            where('status', '==', 'published'),
            orderBy('createdAt', 'desc'), 
            limit(10)
          );
        }
        
        const snapshot = await getDocs(articlesQuery);
        
        if (!snapshot.empty) {
          const fetchedArticles = snapshot.docs.map(doc => {
            const data = doc.data();
            return {
              id: doc.id,
              ...data,
              date: data.createdAt ? new Date(data.createdAt.toMillis()).toLocaleDateString() : 'Unknown date',
              image: data.imageUrl || 'https://placehold.co/600x400?text=GeoDiet+Article'
            };
          });
          setArticles(fetchedArticles);
        } else {
          // No articles found
          setArticles([]);
        }
      } catch (error) {
        console.error('Error fetching articles:', error);
        setArticles([]);
      } finally {
        setLoading(false);
      }
    };

    fetchArticles();
  }, [activeTab]);

  const handleTabChange = (category) => {
    setActiveTab(category);
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const filteredArticles = articles.filter(article => {
    const matchesSearch = article.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          article.excerpt?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          article.content?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesSearch;
  });

  return (
    <JournalContainer>
      <JournalHeader>
        <JournalTitle>GeoDiet Journal</JournalTitle>
        <JournalDescription>Evidence-based articles to support your health journey</JournalDescription>
      </JournalHeader>
      
      <SearchBar>
        <SearchInput 
          type="text" 
          placeholder="Search articles..." 
          value={searchQuery}
          onChange={handleSearchChange}
        />
        <SearchIcon>🔍</SearchIcon>
      </SearchBar>
      
      <FilterTabs>
        {categories.map(category => (
          <FilterTab 
            key={category}
            $active={activeTab === category}
            onClick={() => handleTabChange(category)}
          >
            {category}
          </FilterTab>
        ))}
      </FilterTabs>
      
      {loading ? (
        <div style={{ textAlign: 'center', padding: '50px 0' }}>Loading articles...</div>
      ) : filteredArticles.length > 0 ? (
        <ArticlesGrid>
          {filteredArticles.map(article => (
            <ArticleCard key={article.id}>
              <ArticleImage src={article.image} alt={article.title} />
              <ArticleContent>
                <ArticleCategory 
                  $bgColor={categoryColors[article.category]?.bg}
                  $color={categoryColors[article.category]?.color}
                >
                  {article.category}
                </ArticleCategory>
                <ArticleDate>{article.date}</ArticleDate>
                <ArticleTitle>{article.title}</ArticleTitle>
                <ArticleExcerpt>{article.excerpt}</ArticleExcerpt>
                <ReadArticleLink to={`/journal/${article.id}`}>Read Article</ReadArticleLink>
              </ArticleContent>
            </ArticleCard>
          ))}
        </ArticlesGrid>
      ) : (
        <div style={{ textAlign: 'center', padding: '50px 0' }}>No articles found.</div>
      )}
    </JournalContainer>
  );
};

export default Journal;